import { ChangeEvent, useState, useEffect, useRef } from "react";
import { BiSearch } from "react-icons/bi";
import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";

import { NoResults } from "../NoResults";
import { DropdownItem } from "../DropdownItem";

export interface ISearchItem {
  id: string;
  name: string;
  image: string;
  gameId?: string;
}

const NavSearchBar = ({
  items,
  loading,
  onSelect,
  onFocus,
  debouncedSearch,
  placeholder,
}: {
  items: ISearchItem[];
  loading: boolean;
  onSelect: (item: ISearchItem) => void;
  onFocus: () => void;
  debouncedSearch: (query: string) => void;
  placeholder: string;
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [showMobile, setShowMobile] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setInputValue("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelect = (item: ISearchItem) => {
    onSelect(item);
    setIsOpen(false);
    setShowMobile(false);
    setInputValue("");
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setInputValue(value);
    setIsOpen(value.trim() !== "");
    debouncedSearch(value);
  };

  const handleFocus = () => {
    onFocus();
    if (inputValue.trim() !== "") setIsOpen(true);
  };

  const setMobileFocus = () => {
    setShowMobile(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  return (
    <>
      <MobileStyledSearch onClick={setMobileFocus} />
      <Container ref={wrapperRef} showMobile={showMobile}>
        <Flex alignItems="center" style={{ gap: 16 }}>
          <InputWrapper>
            <StyledSearch />
            <StyledInput
              onChange={handleInputChange}
              onFocus={handleFocus}
              placeholder={placeholder || "Search..."}
              ref={inputRef}
              type="text"
              value={inputValue}
            />
          </InputWrapper>
          <Text
            color="envy"
            display={["block", "block", "none"]}
            fontWeight={500}
            mr={2}
            onClick={() => setShowMobile(false)}
            style={{ zIndex: "1000" }}
          >
            Cancel
          </Text>
        </Flex>
        <SearchContainer isOpen={isOpen}>
          <InputPlaceholder />
          <DropdownWrapper isVisible={isOpen}>
            {items?.length ? (
              items.map((item: ISearchItem) => (
                <DropdownItem
                  key={item.id}
                  avatar={item.image}
                  username={item.name}
                  callback={() => handleSelect(item)}
                />
              ))
            ) : (
              <NoResults loading={loading} />
            )}
          </DropdownWrapper>
        </SearchContainer>
      </Container>
    </>
  );
};

const Container = styled(Box)<{ showMobile: boolean }>`
  cursor: pointer !important;
  position: relative;
  width: 380px;
  z-index: 5;

  @media (max-width: ${(props) => props.theme.breaks.medium}) {
    width: 300px;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    background-color: ${({ theme }) => theme.colors.ned};
    height: 100%;
    left: 0;
    padding: 20px 16px;
    position: absolute;
    top: 0;
    width: 100%;

    display: ${(props) => (props.showMobile ? "block" : "none")};
  }
`;

const InputPlaceholder = styled(Box)`
  height: 40px;
  margin-bottom: 10px;
`;

const SearchContainer = styled(Box)<{ isOpen: boolean }>`
  background-color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.ned : "transparent"};
  border-radius: 20px;
  box-shadow: ${({ isOpen }) =>
    isOpen ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "none"};
  left: 0;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  padding: ${({ isOpen }) => (isOpen ? "10px" : "0")};
  position: absolute;
  top: -12px;
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  width: 100%;
  z-index: 999;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    box-shadow: none;
    opacity: 1;
    top: 12px;
    visibility: visible;
    width: 100vw;
  }
`;

const InputWrapper = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.midnightAlpha50};
  border-radius: 20px;
  border: 2px solid transparent;
  height: 40px;
  justify-content: left;
  margin: 0 auto;
  padding: 3px;
  position: relative;
  width: 96%;
  z-index: 1000;

  &:hover {
    cursor: text;
    background-color: ${({ theme }) => theme.colors.gray850};
  }

  &:focus-within {
    border: 2px solid ${({ theme }) => theme.colors.envy};
  }
`;

const StyledSearch = styled(BiSearch)`
  border-radius: 20px;
  fill: ${({ theme }) => theme.colors.chalk};
  height: 24px;
  padding: 0 0 0 8px;
  width: 24px;
`;

const MobileStyledSearch = styled(BiSearch)`
  display: none;

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: block;
    fill: ${({ theme }) => theme.colors.chalk};
    height: 24px;
    margin-left: auto;
    margin-right: 18px;
    padding: 0 0 0 8px;
    width: 24px;
  }
`;

const StyledInput = styled.input`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 500;
  height: 100%;
  outline: none;
  padding-left: 12px;
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
  }
`;

const DropdownWrapper = styled(Box)<{ isVisible: boolean }>`
  background-color: ${({ theme }) => theme.colors.ned};
  border-radius: 12px;
  display: ${({ isVisible }) => (isVisible ? "block" : "none")};
  height: ${({ isVisible }) => (isVisible ? "auto" : 0)};
  max-height: 250px;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  overflow-y: auto;
  padding-right: 8px;
  transition: opacity 0.2s;
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    display: none;
    position: absolute;
    right: 0px;
    top: 0px;
    width: 12px;
    height: 100%;
    z-index: 999;
    background: #192832;
    border-left: 1px solid #20323f;
  }

  ::-webkit-scrollbar-thumb {
    position: relative;
    display: block;
    height: 20px;
    width: 10px;
    border-radius: 4px;
    margin-left: 2px;
    background-color: #253a49;
    cursor: pointer;
  }

  @media (max-width: ${(props) => props.theme.breaks.small}) {
    max-height: 100vh;
  }
`;

export { NavSearchBar };
